import { useEffect, useState } from 'react';

/**
 * `useState` that persists to `localStorage`. Initial render reads the stored
 * value (when present and JSON-parseable); subsequent writes are flushed back
 * on every change.
 *
 * Cross-tab sync is intentionally not handled — each tab's filter state is
 * naturally independent for the surfaces using this. If a future caller
 * wants real-time sync, add a `storage` listener at that call site rather
 * than coupling every consumer to it here.
 */
export function usePersistedState<T>(key: string, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return initial;
      return JSON.parse(raw) as T;
    } catch {
      // Corrupted entry or quota error — fall back to the supplied default.
      return initial;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Quota exceeded or storage unavailable (private mode etc.) — silently drop.
    }
  }, [key, value]);

  return [value, setValue];
}
